/**
 * @file UserAvatar.tsx
 * @description Reusable component for rendering user avatar.
 */

import React from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../config/firebaseConfig";
import PersonIcon from "@mui/icons-material/Person";
import { useUserDetails } from "../../context/UserDetailsContext";
import CrownIcon from "./icons/CrownIcon";

interface UserAvatarProps {
  size?: string;
  showIndicator?: boolean;
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  size = "w-12",
  showIndicator = true,
}) => {
  const [user] = useAuthState(auth);
  const { userDetails } = useUserDetails();

  const baseAvatarClass =
    "btn btn-circle w-auto h-auto avatar outline-none bg-transparent p-0 border-0 hover:bg-transparent hover:outline-none hover:border-0 h-content w-content";
  const premiumClass =
    showIndicator && userDetails?.isPremium ? "indicator" : "";
  const placeholderClass = "placeholder";
  const indicatorSVG = (
    <CrownIcon className="indicator-item indicator-top indicator-start" />
  );

  const getAvatarClass = (isPlaceholder: boolean) => {
    return `${baseAvatarClass} ${premiumClass} ${
      isPlaceholder ? placeholderClass : ""
    }`;
  };

  if (userDetails?.photoURL) {
    return (
      <div tabIndex={0} role="button" className={getAvatarClass(false)}>
        {showIndicator && userDetails.isPremium && indicatorSVG}
        <div className={`${size} rounded-full`}>
          <img src={userDetails.photoURL} alt="avatar" />
        </div>
      </div>
    );
  } else if (userDetails?.firstName && userDetails.lastName) {
    const initials = userDetails.firstName[0] + userDetails.lastName[0];
    return (
      <div tabIndex={0} role="button" className={getAvatarClass(true)}>
        {showIndicator && userDetails.isPremium && indicatorSVG}
        <div className={`${size} rounded-full bg-neutral text-neutral-content`}>
          <span className="text-xl">{initials.toUpperCase()}</span>
        </div>
      </div>
    );
  } else if (user?.email) {
    const emailInitial = user.email[0];
    return (
      <div tabIndex={0} role="button" className={getAvatarClass(true)}>
        {showIndicator && userDetails?.isPremium && indicatorSVG}
        <div className={`${size} rounded-full bg-neutral text-neutral-content`}>
          <span className="text-xl">{emailInitial.toUpperCase()}</span>
        </div>
      </div>
    );
  } else {
    return (
      <div tabIndex={0} role="button" className={getAvatarClass(true)}>
        {showIndicator && userDetails?.isPremium && indicatorSVG}
        <div
          className={`${size} rounded-full bg-primary text-white text-opacity-75`}
        >
          <PersonIcon className="object-cover w-full h-full p-2" />
        </div>
      </div>
    );
  }
};

export default UserAvatar;
